import { useState, useEffect } from "react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { HttpMethod, callApi } from "src/services/apiService";
import Loading from "src/components/Loading";
import Warning from "src/components/Warning";
import SaveInProgress from "src/components/SaveInProgress";
import {
  IContactInfo,
  ContactInfoDetails,
} from "src/interfaces/Admin/IContactInfo";
import statesData from "src/data/regionsUSA.json";
import SafeHtml from "src/components/SafeHtml";
import {
  isValidEmailAddress,
  emailValidationRules,
  optionalEmailValidationRules,
} from "src/utils/emailValidation";

const ContactInfo = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [warningMessage, setWarningMessage] = useState("");
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [formData, setFormData] = useState<ContactInfoDetails>();
  const [states] = useState(statesData);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<ContactInfoDetails>({ mode: "onChange" });

  const watchedEmail = watch("email");

  const [originalData, setOriginalData] = useState<IContactInfo | null>(null);

  useEffect(() => {
    const fetchContactInfo = async () => {
      setIsLoading(true);
      try {
        const apiUrl = "admin/contactinfo";
        const response = await callApi(apiUrl, HttpMethod.GET);
        setFormData(response.contactInfo);
        setOriginalData(response);
        const contactInfoData = response.contactInfo;
        reset(contactInfoData);
      } catch (error) {
        console.error("Failed to fetch contact info:", error);
        setWarningMessage("Failed to fetch contact info.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchContactInfo();
  }, [reset]);

  //Abhi K - Had to separate it out as it was causing a loop
  useEffect(() => {
    if (formData?.email) {
      setConfirmEmail(formData.email);
    }
  }, [formData?.email]);

  useEffect(() => {
    if (confirmEmail && !isValidEmailAddress(confirmEmail)) {
      setConfirmEmailError("Invalid email format");
    } else if (watchedEmail && confirmEmail && watchedEmail !== confirmEmail) {
      setConfirmEmailError("Email addresses do not match");
    } else {
      setConfirmEmailError("");
    }
  }, [confirmEmail, watchedEmail]);

  const handleRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setValue("region", event.target.value, { shouldDirty: true });
  };

  const onSubmit = async (formData: ContactInfoDetails) => {
    // Check if email and confirm email match
    if (formData.email !== confirmEmail) {
      setConfirmEmailError("Email addresses do not match");
      return;
    }

    setSaveInProgress(true);

    try {
      const apiUrl = `admin/contactinfo`;
      const method = HttpMethod.POST;
      const body = formData;

      await callApi(apiUrl, method, body);
      console.log("Settings saved successfully");
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
    setSaveInProgress(false);
  };

  const handleReset = () => {
    reset(originalData?.contactInfo);
    setConfirmEmail(originalData?.contactInfo?.email || "");
  };

  if (isLoading) {
    return <Loading />;
  }

  if (warningMessage) {
    return <Warning message={warningMessage} />;
  }

  return (
    <div className="pb-5" style={{ maxWidth: "960px" }}>
      <h6>My Contact Info</h6>
      {formData && originalData && (
        <>
          <p className="text-muted">
            <SafeHtml html={originalData?.description.long} />
          </p>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="card">
              <div className="card-header">Primary Contact</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="ci-firstName" className="form-label">
                      First Name
                    </label>
                    <input
                      id="ci-firstName"
                      {...register("firstName", {
                        required: "First Name is required",
                      })}
                      className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.firstName?.message}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="ci-lastName" className="form-label">
                      Last Name
                    </label>
                    <input
                      id="ci-lastName"
                      {...register("lastName", {
                        required: "Last Name is required",
                      })}
                      className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.lastName?.message}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="ci-title" className="form-label">
                      Title
                    </label>
                    <input
                      id="ci-title"
                      {...register("title")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="ci-organization" className="form-label">
                      Organization Name
                    </label>
                    <input
                      id="ci-organization"
                      {...register("organization", {
                        required: "Organization Name is required",
                      })}
                      className={`form-control ${errors.organization ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.organization?.message}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">Address</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="ci-address1" className="form-label">
                      Address 1
                    </label>
                    <input
                      id="ci-address1"
                      {...register("address1", {
                        required: "Address 1 is required",
                      })}
                      className={`form-control ${errors.address1 ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.address1?.message}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="ci-address2" className="form-label">
                      Address 2
                    </label>
                    <input
                      id="ci-address2"
                      {...register("address2")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="ci-city" className="form-label">
                      City
                    </label>
                    <input
                      id="ci-city"
                      {...register("city", { required: "City is required" })}
                      className={`form-control ${errors.city ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.city?.message}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ci-region" className="form-label">
                      State
                    </label>
                    <select
                      id="ci-region"
                      {...register("region")}
                      defaultValue={formData?.region}
                      className="form-select"
                      onChange={handleRegionChange}
                    >
                      {states.map((state) => (
                        <option key={state.abbrev} value={state.abbrev}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="ci-postCode" className="form-label">
                      Zip
                    </label>
                    <input
                      id="ci-postCode"
                      {...register("postCode")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ci-country" className="form-label">
                      Country
                    </label>
                    <input
                      id="ci-country"
                      {...register("country")}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">Phone &amp; Web</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label htmlFor="ci-phoneNumber" className="form-label">
                      Phone Number
                    </label>
                    <input
                      id="ci-phoneNumber"
                      {...register("phoneNumber")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="ci-phoneExt" className="form-label">
                      Ext
                    </label>
                    <input
                      id="ci-phoneExt"
                      {...register("phoneExt")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ci-faxNumber" className="form-label">
                      Fax Number
                    </label>
                    <input
                      id="ci-faxNumber"
                      {...register("faxNumber")}
                      className="form-control"
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="ci-url" className="form-label">
                      Web Site URL
                    </label>
                    <input
                      id="ci-url"
                      {...register("url")}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">Email</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="ci-email" className="form-label">
                      Email Address
                    </label>
                    <input
                      id="ci-email"
                      {...register("email", emailValidationRules)}
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    />
                    <div className="invalid-feedback">
                      {errors.email?.message}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="ci-confirmEmail" className="form-label">
                      Confirm Email Address
                    </label>
                    <input
                      id="ci-confirmEmail"
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      className={`form-control ${confirmEmailError ? "is-invalid" : ""}`}
                      placeholder="Confirm Email"
                    />
                    <div className="invalid-feedback">{confirmEmailError}</div>
                  </div>
                  <div className="col-12">
                    <label className="form-label mb-1">
                      Additional Email Addresses
                    </label>
                    <div className="form-text mt-0 mb-2">
                      Up to four additional e-mail addresses to receive account
                      communications, legal updates, etc.
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <input
                          {...register("email2", optionalEmailValidationRules)}
                          className={`form-control ${errors.email2 ? "is-invalid" : ""}`}
                          aria-label="Additional email 1"
                        />
                        <div className="invalid-feedback">
                          {errors.email2?.message}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <input
                          {...register("email3", optionalEmailValidationRules)}
                          className={`form-control ${errors.email3 ? "is-invalid" : ""}`}
                          aria-label="Additional email 2"
                        />
                        <div className="invalid-feedback">
                          {errors.email3?.message}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <input
                          {...register("email4", optionalEmailValidationRules)}
                          className={`form-control ${errors.email4 ? "is-invalid" : ""}`}
                          aria-label="Additional email 3"
                        />
                        <div className="invalid-feedback">
                          {errors.email4?.message}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <input
                          {...register("email5", optionalEmailValidationRules)}
                          className={`form-control ${errors.email5 ? "is-invalid" : ""}`}
                          aria-label="Additional email 4"
                        />
                        <div className="invalid-feedback">
                          {errors.email5?.message}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label
                      htmlFor="ci-emailSanExpiration"
                      className="form-label mb-1"
                    >
                      SAN Expiration Warning Email
                    </label>
                    <div className="form-text mt-0 mb-2">
                      If not provided, warnings are sent to the primary contact
                      above.
                    </div>
                    <input
                      id="ci-emailSanExpiration"
                      {...register("emailSanExpiration")}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isDirty}
              >
                {!saveInProgress ? (
                  "Save Contact Info"
                ) : (
                  <SaveInProgress isVisible={saveInProgress} />
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary ms-2"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ContactInfo;
